import { GraphQLError } from "graphql"
import { groq } from "next-sanity"

import { groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { groqArticleProjection } from "../../shared/sanity/article.projection"
import { getEnrichedArticles } from "../../shared/utils/getArticleInformation"

/**
 * Retrieves articles based on the provided arguments.
 *
 * @param {Object} parent - The parent object.
 * @param {Object} args - The arguments object.
 * @param {Object} context - The context object.
 * @param {Object} info - The info object.
 * @returns {Object} - The articles and related information.
 */
export const queryArticles = {
  async articles(parent, args, context, info) {
    const {
      offset = 0,
      limit = 25,
      sort,
      lang = AppSettings.defaultLocale(),
      cdn = true,
      sortWithinLimits = false,
      customerNumber,
      loggedInBranch,
      search,
      articleGroup,
      articleMainGroup,
      status,
      brand,
    } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ARTICLES,
      operation: Operations.READ,
    })
    setDefaults(search)

    const articleSearchString = getArticleSearchQueryString({ search })

    if (search && articleSearchString === "") {
      throw new GraphQLError("Invalid search string for selected field", {})
    }

    const articleFilter = getFilterForArticleAttributes({
      articleGroup,
      articleMainGroup,
      status,
      loggedInBranch,
      brand,
    })

    const articles = await getArticlesFromSanity({
      context,
      articleSearchString,
      articleFilter,
      offset,
      limit,
      sort,
      lang,
      cdn,
      sortWithinLimits,
      searchString: search.searchString,
    })

    articles.items = await getEnrichedArticles({
      context,
      articles: articles?.items,
      branchId: loggedInBranch,
      customerNumber: customerNumber,
      customerId: null,
    })

    return {
      ...articles,
      offset,
      limit,
      count: articles?.items?.length,
    }
  },
}

/**
 * Sets default values for the search object.
 *
 * @param {Object} search - The search object.
 */
function setDefaults(search) {
  if (search) {
    const shouldBeSearchedInAllFields = !search?.searchInFields || Object.keys(search.searchInFields).length === 0

    if (shouldBeSearchedInAllFields) {
      search.searchInFields = {
        title: true,
        altTitle: true,
        articleInfo: true,
        brand: true,
        titleSearchTerm: true,
        searchTermThree: true,
        searchTermFour: true,
        sku: true,
        assortment: true,
        ean: true,
      }
    }
  }
}

/**
 * Generates the search query string for articles.
 *
 * @param {Object} search - The search object.
 * @returns {string} - The search query string.
 */
function getArticleSearchQueryString({ search }) {
  if (!search?.searchString) return ""

  const stringMatcher = getArticleMatchersForStingValues({ search })
  const numberMatcher = getArticleMatchersForNumberValues({ search })

  const searchString = `${stringMatcher}${numberMatcher}`
  if (searchString.length > 0) {
    return `&& (${searchString})`
  }
  return ""
}

/**
 * Generates the string matchers for string values in the article search.
 *
 * @param {Object} search - The search object.
 * @returns {string} - The string matchers.
 */
function getArticleMatchersForStingValues({ search }) {
  if (includesNumbersOnly(search.searchString)) return ""
  const { searchInFields } = search
  const matchers = []
  if (searchInFields?.title) matchers.push(`title match '*'+$term+'*'`)
  if (searchInFields?.altTitle) matchers.push(`altTitle match '*'+$term+'*'`)
  if (searchInFields?.articleInfo) matchers.push(`articleInfo match '*'+$term+'*'`)
  if (searchInFields?.brand) matchers.push(`brand match '*'+$term+'*'`)
  if (searchInFields?.titleSearchTerm) matchers.push(`titleSearchTerm match '*'+$term+'*'`)
  if (searchInFields?.searchTermThree) matchers.push(`searchTermThree match '*'+$term+'*'`)
  if (searchInFields?.searchTermFour) matchers.push(`searchTermFour match '*'+$term+'*'`)

  return matchers.length ? `${matchers.join(" || ")} ` : ""
}

/**
 * Generates the string matchers for number values in the article search.
 *
 * @param {Object} search - The search object.
 * @returns {string} - The string matchers.
 */
function getArticleMatchersForNumberValues({ search }) {
  if (!includesNumbersOnly(search.searchString)) return ""
  const { searchInFields } = search
  const matchers = []
  if (searchInFields?.sku) matchers.push(`sku match '*'+$term+'*'`)
  if (searchInFields?.assortment) matchers.push(`assortment match '*'+$term+'*'`)
  if (searchInFields?.searchTermThree) matchers.push(`searchTermThree match '*'+$term+'*'`)
  if (searchInFields?.ean) matchers.push(`ean match '*'+$term+'*'`)
  if (searchInFields?.searchTermFour) matchers.push(`searchTermFour match '*'+$term+'*'`)

  return matchers.length ? `${matchers.join(" || ")}` : ""
}

/**
 * Generates the filter for article attributes.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.articleGroup - The article group.
 * @param {string} params.articleMainGroup - The article main group.
 * @param {string} params.status - The status.
 * @param {string} params.loggedInBranch - The logged in branch.
 * @param {Object} params.client - The Sanity client.
 * @param {string} params.brand - The brand.
 * @returns {string} - The filter string.
 */
function getFilterForArticleAttributes({ articleGroup, articleMainGroup, status, loggedInBranch, brand }) {
  const filter = []

  if (articleGroup) filter.push(`"${articleGroup}" == articleGroup._ref `)
  if (articleMainGroup) filter.push(`"${articleMainGroup}" == articleMainGroup._ref `)
  if (status) filter.push(`status == "${status}"`)
  if (loggedInBranch) filter.push(`!("branchOffice-${loggedInBranch}" in notAvailableInBranchOffices[]._ref)`)
  if (brand) {
    filter.push(`brand match "${brand}"`)
  }
  return filter.length ? "&& " + filter.join(" && ") : ""
}

/**
 * Retrieves articles from Sanity.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.context - The context object.
 * @param {string} params.articleSearchString - The article search string.
 * @param {string} params.articleFilter - The article filter.
 * @param {number} params.offset - The offset.
 * @param {number} params.limit - The limit.
 * @param {string} params.sort - The sort.
 * @param {string} params.lang - The language.
 * @param {boolean} params.cdn - The CDN flag.
 * @param {boolean} params.sortWithinLimits - The sort within limits flag.
 * @param {string} options.searchString - The search string.
 * @returns {Object} - The articles.
 */
async function getArticlesFromSanity({
  context,
  articleSearchString,
  articleFilter,
  offset,
  limit,
  sort,
  lang,
  cdn,
  sortWithinLimits,
  searchString,
}) {
  const config = AppSettings.sanityConfigByLang(lang)
  const client = context.sanityClient
  client.config({ ...config, useCdn: cdn })

  const { pipesAndLimits, draftsFilter } = groqQueryOptions({
    sort,
    offset,
    limit,
    sortWithinLimits,
    includeDrafts: true,
  })

  const totalCountQuery = groq`count(*[_type=="article"  ${articleFilter} ${articleSearchString}${draftsFilter}])`
  const articlesQuery = groq`*[_type=="article" ${articleFilter} ${articleSearchString}${draftsFilter}]${pipesAndLimits}{
    ...,
    ${groqArticleProjection()}
  }`

  const [totalCount, articleItems] = await Promise.all([
    client.fetch(totalCountQuery, { term: searchString }),
    client.fetch(articlesQuery, { term: searchString }),
  ])

  const articles = {
    total: totalCount,
    items: articleItems,
  }

  return articles
}

/**
 * Checks if a string includes only numbers.
 *
 * @param {string} searchString - The search string.
 * @returns {boolean} - True if the string includes only numbers, false otherwise.
 */
function includesNumbersOnly(searchString) {
  return !isNaN(searchString)
}
